import * as $ from "jquery";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import "select2";
import { DashboardChannel } from "../../../../models/DashboardChannel";
import { getAggFunctionName } from "../../../reports/ReportUtils";
import { GetSeriesOptions } from "../ChartWorkerManager";
import { BaseDashboardWidgetView, BaseDashboardWidgetViewOptions } from "../_BaseWidgetView";
import {
    CHART_AGGREGATION_PERIOD,
    CHART_AGGREGATION_TYPE,
    CHART_REAL_TIMESTAMPS,
    CHART_SIZE,
    CHART_TYPE,
    ChartConfig,
    ChartHelper,
    minMaxTimeStampOptions
} from "../chart/ChartHelper";

declare var App: any;

export class TableWidgetView extends BaseDashboardWidgetView {

    protected template = _.template(require('templates/reports/widgets/table/table.html'));

    private tableChannels: Backbone.Model[];
    private tableDashboardChannelIds: number[];
    private tableData: {[dashboadChannelId: number]: any};
    private minDataRequested: number = Infinity;
    private maxDataRequested: number = null;
    private dateTimePickerFormat = 'MM/DD/YYYY h:mm A';
    private MAX_CHANNELS_TO_SHOW = 4;
    private useCustomLabels = true;

    constructor(options: BaseDashboardWidgetViewOptions) {
        super(<any> _.extend({
            tagName: "div",
            className: 'widget',
            events: {
                'input .chart-data-range input': 'handleTimePickerChange',
                'dp.change .chart-data-range .date': 'handleTimePickerChange',
                'click .chart-data-range .reset': 'handleTimeSelectorReset',

                // Widget related buttons
                'click .w-settings .remove': 'handleRemoveWidgetClick',
                'click .w-delete-widget .w-cancel': 'handleRemoveWidgetCancelClick',
                'click .w-delete-widget .w-confirm': 'handleRemoveWidgetConfirmClick',
                'click .w-settings .gear': 'handleEditWidgetClick',
                'click .w-settings .clone': 'handleCloneWidget'
            }
        }, options));

        const rawUseCustomLabelsConfig = this.options.widget.getParamValue('USE_CUSTOM_LABELS', true);
        // coerce the raw value -- which may be a boolean, number, or string -- into a boolean
        this.useCustomLabels = !!Number(rawUseCustomLabelsConfig);
    }

    render() {
        this.$el.html(this.template({
            widget: this.options.widget,
            reportId: this.options.report.get('id'),
            permissionCollection: this.options.permissionCollection,
            editable: this.options.editable,
            movable: this.options.movable,
            cloneable: this.options.cloneable,
            deletable: this.options.deletable
        }));

        this.$el.addClass('widget-w-' + this.options.widget.getParamValue('CHART_SIZE', CHART_SIZE.SIZE_6));
        this.$el.attr('data-widget-id', this.options.widget.get('id'));

        this.tableData = {};
        // Get channels for this table
        var widgetId = this.options.widget.get('id');
        this.tableChannels = <DashboardChannel[]>this.options.dashboardChannelCollection.where({
            dashboard_widget_id: widgetId
        });
        this.tableDashboardChannelIds = _.map(this.tableChannels, (channel) => channel.get('dashboard_channel_id'));

        if (this.tableChannels.length <= this.MAX_CHANNELS_TO_SHOW) {
            $.when(this.getData(this.tableDashboardChannelIds)).then(() => {
                this.renderTableColumnsAndRows();
            });
        } else {
            setTimeout(() => {
                this.renderTableColumnsAndRows();
            }, 200)
        }

        this.delegateEvents();
        return this;
    }

    private setLoading(loading: boolean) {
        this.$('.w-table .modal-form-spinner').toggleClass('hidden', !loading);
        this.$('.w-table .table').toggleClass('hidden', loading);
    }

    getData(dashboardChannelIds: number[]) {
        var deferred = $.Deferred();

        var tasks = [];
        var period = this.options.widget.getParamValue('CHART_AGGREGATION_PERIOD', CHART_AGGREGATION_PERIOD.DEFAULT);

        _.each(dashboardChannelIds, (id) => {
            var getSeriesDeferred = $.Deferred();
            tasks.push(getSeriesDeferred);
            var channel = this.options.dashboardChannelCollection.findWhere({dashboard_channel_id: Number(id)});
            var initial = this.minDataRequested === Infinity;

            var baseMinMaxOptions = {
                initial: initial,
                channel: channel,
                widget: this.options.widget,
                customDataRangeFunc: null,
                isDefaultChart: false,
                dashboardChannelCollection: this.options.dashboardChannelCollection,
                graphedDashboardChannelIds: this.tableDashboardChannelIds,
                user: this.options.currentUser
            };
            var maxOptions: minMaxTimeStampOptions = _.extend(baseMinMaxOptions, {
                maxDataRequested: this.maxDataRequested
            });
            var max = ChartHelper.getMaxTimestampToRequest(maxOptions);
            if (max > this.maxDataRequested) this.maxDataRequested = max;

            var minOptions: minMaxTimeStampOptions = _.extend(baseMinMaxOptions, {
                minDataRequested: this.minDataRequested,
                maxDataRequested: this.maxDataRequested
            });

            var min = ChartHelper.getMinTimestampToRequest(minOptions);


            if (min < this.minDataRequested) this.minDataRequested = min;
            var dashboardChannel = this.options.dashboardChannelCollection.findWhere({dashboard_channel_id: id});
            var aggregationParam = this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', CHART_AGGREGATION_TYPE.DEFAULT);
            var aggregationTypes = _.map(_.split(aggregationParam, ','));
            var order = dashboardChannel.get('dashboard_channel_order');

            var aggType;
            if (period === CHART_AGGREGATION_PERIOD.NONE) {
                aggType = CHART_AGGREGATION_TYPE.NONE;
            } else if (order && order < aggregationTypes.length) {
                aggType = aggregationTypes[order];
            } else {
                aggType = aggregationTypes[0];
            }

            var series = [{
                agg: aggType,
                tag: id,
                channelId: dashboardChannel.get('channel_id'),
                name: dashboardChannel.get('channel_name')
            }];
            var chartConfig: ChartConfig = {
                seriesGroups: [{
                    type: CHART_TYPE.WIDGET_MULTI_SERIES,
                    period: period,
                    series: series,
                }],
                timezone: ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser),
                showThresholdPlotlines: false,
                realTimestamps: ChartHelper.isEnabled(this.options.widget.getParamValue('CHART_TIMESTAMPS', CHART_REAL_TIMESTAMPS.DEFAULT)),
                hideSingleGaps: true,
                convertAllValues: true,
                labels: false
            };

            var getSeriesChannels = {};
            getSeriesChannels[id] = {
                id: dashboardChannel.get('channel_id'),
                sensor_type: dashboardChannel.get('sensor_type'),
                sensor_connector: dashboardChannel.get('sensor_connector'),
                sensor_version: dashboardChannel.get('sensor_version'),
                position: dashboardChannel.get('channel_position'),
                channel_secret: dashboardChannel.get('channel_secret'),
                last_timestamp: dashboardChannel.get('channel_last_timestamp') || moment().unix(),
                polling_frequency: dashboardChannel.get('module_polling_frequency'),
                tags: [id]
            };

            var getSeriesOptions: GetSeriesOptions = {
                chartConfig: chartConfig,
                channels: getSeriesChannels,
                startTimestamp: this.minDataRequested,
                endTimestamp: this.maxDataRequested,
            };
            $.when(this.options.chartWorkerManager.getSeries(getSeriesOptions)).then((data) => {
                // Keyed by chart type
                // Multi channel series is located at index 0
                this.tableData = _.extend(this.tableData, data[CHART_TYPE.WIDGET_MULTI_SERIES][0]);
                _.each(this.tableData, (tableData, dashboardId) => {
                    this.tableData[dashboardId] = _.sortBy(tableData, '0')
                });
                getSeriesDeferred.resolve();
            });
        });

        $.when.apply($, tasks).then(() => {
            deferred.resolve();
        });
        return deferred;
    }

    private renderColumns(rows) {
        var columns = [`<tr><td class="text-center">Time</td>`];
        const aggTypes = this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', '').split(',');


        // Add column title with name of each channel
        _.each(this.tableData, (data, dashbaordChannelId) => {
            var channel = this.options.dashboardChannelCollection.findWhere({
                dashboard_channel_id: Number(dashbaordChannelId)
            });
            const channelOrder = channel.get('dashboard_channel_order');
            const aggFunctionName = getAggFunctionName(aggTypes[channelOrder]);
            let channelLabel = channel.get('dashboard_channel_name');
            if(!this.useCustomLabels) channelLabel = `${channel.get('module_location')} - ${channel.get('channel_name')}`;
            var columnName = `${channelLabel} - ${channel.get('channel_unit')} (${aggFunctionName})`
            columns.push(`<td class="text-center"> ${_.escape(columnName)} </td>`);
        });

        columns.push(`</tr>`);

        rows.push(columns.join(''));
    }

    private renderRows(rows) {
        var dataRows: { [timestamp: number]: any[] } = {};
        _.each(_.keys(this.tableData), (dashboardChannelId, i) => {
            _.each(this.tableData[dashboardChannelId], (point) => {
                var date = moment.tz(point[0], this.getTimezone()).format(this.dateTimePickerFormat);
                if (!dataRows[point[0]]) dataRows[point[0]] = [date];

                // do this for missing data or duplicate timestamps
                dataRows[point[0]][i + 1] = point[1] ? _.round(point[1], 2) : '';

            });
        });

        // Create rows
        let sortedRows = [];
        _.each(_.sortBy(_.keys(dataRows), (ts) => {return ts}), (timestamp) => {
            sortedRows.push(dataRows[timestamp])
        });
        var templateRows = [];
        _.each(sortedRows, (dataValues: any[], i) => {
            var rowString = `<tr>`;
            var dataStrings = _.map(dataValues, (value) => {
                return `<td class="text-center">${value ? _.escape(value) : '' }</td>`
            });

            rowString += dataStrings.join('');
            rowString += `</tr>`;

            if(i === 10) {
                rowString = `<tr><td class="text-center" colspan=${dataValues.length}> And ${sortedRows.length - 10 } Additional rows</td></tr>`;
            }

            // hide really long tables
            if(i > 10) return;
            templateRows.push(rowString);
        });

        rows.push(templateRows.join(''));
    }

    private renderTableColumnsAndRows() {
        this.setLoading(false);

        // If there are 4 or fewer channels, display table in UI
        if (this.tableChannels.length <= this.MAX_CHANNELS_TO_SHOW) {
            var rows = [];
            this.renderColumns(rows);
            this.renderRows(rows);
            this.$('.w-table .table').html(rows.join(''));
        // If it's larger, display a list of channels used
        } else {
            var widgetHeader = '<h3 class="text-center">Report Table</h3><h4 class="text-center" style="color: #114257;">' + _.escape(this.options.widget.getParamValue('CHART_TITLE', '')) + '</h4>';
            var channelList = _.map(this.tableChannels, (channel) => {
                let channelLabel = channel.get('dashboard_channel_name');
                if(!this.useCustomLabels) channelLabel = `${channel.get('module_location')} - ${channel.get('channel_name')}`;

                return '<li>' + _.escape(channelLabel) + '</li>';
            }).join('');

            this.$('.w-table .table').html(
                widgetHeader +
                '<div class="text-center">' +
                '<h5 style="font-style: italic;">Including channels:</h5>' +
                '<ul style="list-style: none;">' +
                channelList +
                '</ul>' +
                '</div>');
        }

        // Tell Packery to do its magic again after the template is loaded, so that our widgets are re-organized
        App.Bus.trigger('dashboard:category:' + this.options.categoryId + ':layout');
    }

    private getTimezone() {
        return this.options.widget.getParamValue('CHART_TIMEZONE', this.options.currentUser.get('timezone'));
    }

    remove() {
        // Terminate web workers and unload everything
        this.destroy();
        return super.remove();
    }

    destroy() {
        this.minDataRequested = Infinity;
        this.maxDataRequested = null;
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/dashboard/widgets/table/TableWidgetView.ts